import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuthStore } from '@/stores/authStore';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const IntakePage = lazy(() => import('@/features/intake/pages/IntakePage'));
const TicketListPage = lazy(() => import('@/features/tickets/pages/TicketListPage'));
const TicketDetailPage = lazy(() => import('@/features/tickets/pages/TicketDetailPage'));
const CustomerListPage = lazy(() => import('@/features/customers/pages/CustomerListPage'));
const CustomerDetailPage = lazy(() => import('@/features/customers/pages/CustomerDetailPage'));
const PartsPage = lazy(() => import('@/features/parts/pages/PartsPage'));
const TechniciansPage = lazy(() => import('@/features/technicians/pages/TechniciansPage'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage'));
const SalesPage = lazy(() => import('@/features/sales/pages/SalesPage'));
const SaleDetailPage = lazy(() => import('@/features/sales/pages/SaleDetailPage'));
const NewSalePage = lazy(() => import('@/features/sales/pages/NewSalePage'));
const InvoicesPage = lazy(() => import('@/features/invoices/pages/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/features/invoices/pages/InvoiceDetailPage'));
const NewInvoicePage = lazy(() => import('@/features/invoices/pages/NewInvoicePage'));
const PaymentsPage = lazy(() => import('@/features/payments/pages/PaymentsPage'));
const PaymentDetailPage = lazy(() => import('@/features/payments/pages/PaymentDetailPage'));
const CommissionsPage = lazy(() => import('@/features/commissions/pages/CommissionsPage'));
const AIDiagnosisPage = lazy(() => import('@/features/ai/pages/AIDiagnosisPage'));
const AIQuickIntakePage = lazy(() => import('@/features/ai/pages/AIQuickIntakePage'));
const QueuePage = lazy(() => import('@/features/queue/pages/QueuePage'));
const CashPage = lazy(() => import('@/features/cash/pages/CashPage'));
const ShiftDetailPage = lazy(() => import('@/features/cash/pages/ShiftDetailPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner size="lg" />
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes with layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="intake" element={<IntakePage />} />
            <Route path="tickets" element={<TicketListPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="customers" element={<CustomerListPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="inventory" element={<PartsPage />} />
            <Route path="technicians" element={<TechniciansPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="sales/new" element={<NewSalePage />} />
            <Route path="sales/:id" element={<SaleDetailPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="invoices/new" element={<NewInvoicePage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="payments/:id" element={<PaymentDetailPage />} />
            <Route path="commissions" element={<CommissionsPage />} />
            <Route path="queue" element={<QueuePage />} />
            <Route path="ai/diagnosis" element={<AIDiagnosisPage />} />
            <Route path="ai/quick-intake" element={<AIQuickIntakePage />} />
            <Route path="cash" element={<CashPage />} />
            <Route path="cash/shifts/:id" element={<ShiftDetailPage />} />
          </Route>

          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
